import { useEffect } from "react";
import { useFormikContext } from "formik";
import { unstable_useBlocker as useBlocker } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { useTranslation } from "react-i18next";

interface Props {
  message?: string;
}

export default function FormUnsavedChangesGuard({
  message = "You have unsaved changes. Do you really want to leave?",
}: Props) {
  const { dirty } = useFormikContext();
  const blocker = useBlocker(dirty);
  const { t } = useTranslation();

  // Show MUI dialog when internal navigation is blocked
  const open = blocker.state === "blocked";

  const handleConfirm = () => {
    // @ts-ignore
    blocker.proceed(); // Allow navigation
  };

  const handleCancel = () => {
    // @ts-ignore
    blocker.reset(); // Stay on page
  };

  // Show native browser alert on tab close / refresh
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = message;
      return message;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [dirty, message]);

  return (
    <Dialog open={open} onClose={handleCancel}>
      <DialogTitle>{t('Unsaved Changes')}</DialogTitle>
      <DialogContent>{message}</DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="primary">
          {t('Stay')}
        </Button>
        <Button onClick={handleConfirm} color="error">
          {t('Leave')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}